import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAppointmentsTableReportData } from "../../../../redux/reducers/reportSlice";
import { getClinicVets } from "../../../../redux/reducers/vetSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import {
  appointmentStatusList,
  scheduleImmediateList,
  serviceTypes,
  typeAppointemnt,
  typeList,
  visitTypes,
  yesNoBoolList,
} from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./AppointmentTrends.css";

const tableHdrs = [
  "appointmentDateTime",
  "vetName",
  "petName",
  "caseId",
  "serviceType",
  "visitType",
  "typeOfConsult",
  "modeOfBooking",
  "timeSpent",
  "delay",
  "waitTime",
  "scheduledOrImmediate",
  "rescheduled",
  "status",
  "revenue",
  "rating",
  "cancellationReason",
  "rescheduledReason",
  "assignedUsers",
];
const initSearch = {
  vetName: "",
  petName: "",
  caseId: "",
  serviceType: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Appointment Date",
  "Visit Type",
  "Consultation Mode",
  "Booking Type",
  "Time Spent",
  "Delay",
  "Wait Time",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
  "Revenue",
  "Rating",
  "Cancellation Reason",
  "Rescheduling Reason",
  "Assigned Users",
];
const dropFields = [
  "Visit Type",
  "Consultation Mode",
  "Booking Type",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
  "Assigned Users",
];

const AppointmentsTableReport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("2025-08 to 2026-08");
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { appointmentTable } = useSelector((state) => state?.report);
  const vets = useSelector((state) => state?.vet?.vets);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getClinicVets("?type=all"));
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    dateRange,
    selectedPeriod,
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.caseId,
    searchValues?.serviceType,
  ]);

  const trendsData = appointmentTable?.appointmentTrends?.data || appointmentTable?.appointmentTrends || {};

  // 1. Summary Cards Parsing
  const cardsObj = trendsData?.cards || {};

  const appointmentsVal =
    cardsObj?.appointments ??
    trendsData?.appointmentsDisplay ??
    trendsData?.appointments ??
    "228";

  const revenueVal =
    cardsObj?.revenueDisplay
      ? `₹${cardsObj.revenueDisplay}`
      : cardsObj?.revenue !== undefined
      ? `₹${(cardsObj.revenue / 100000).toFixed(2)}L`
      : "₹1.83L";

  const noShowRateVal =
    cardsObj?.noShowRate !== undefined
      ? `${cardsObj.noShowRate}%`
      : "5.1%";

  const rescheduleRateVal =
    cardsObj?.rescheduleRate !== undefined
      ? `${cardsObj.rescheduleRate}%`
      : "4.3%";

  const summaryCards = [
    { title: "Appointments", value: `${appointmentsVal}` },
    { title: "Revenue (period)", value: `${revenueVal}` },
    { title: "No-show rate", value: `${noShowRateVal}` },
    { title: "Reschedule rate", value: `${rescheduleRateVal}` },
  ];

  // 2. Line Chart 1: Volume vs last year Parsing
  const volumeData = Array.isArray(trendsData?.appointmentVolume)
    ? trendsData.appointmentVolume
    : null;

  let volumeCategories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let thisYearVolume = [175, 205, 255, 225, 250, 275, 290, 210, 0, 0, 0, 0];
  let lastYearVolume = [145, 175, 215, 200, 215, 235, 255, 270, 245, 225, 210, 230];

  if (volumeData && volumeData.length > 0) {
    volumeCategories = volumeData.map((item) => String(item.month || ""));
    thisYearVolume = volumeData.map((item) => Number(item.thisYear ?? 0));
    lastYearVolume = volumeData.map((item) => Number(item.lastYear ?? 0));
  }

  const maxVolume = Math.max(...thisYearVolume, ...lastYearVolume, 300);

  const volumeChartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5, dashArray: [0, 5] },
    colors: ["#6366F1", "#10B981"],
    xaxis: {
      categories: volumeCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    yaxis: {
      min: 0, max: maxVolume, tickAmount: 6,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    legend: { position: "top", horizontalAlign: "left", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const volumeChartSeries = [
    { name: "This year", data: thisYearVolume },
    { name: "Last year", data: lastYearVolume },
  ];

  // 3. Grouped Bar Chart 2: Disruptions Parsing
  const disruptionsData = Array.isArray(trendsData?.disruptions)
    ? trendsData.disruptions
    : null;

  let noShowsCategories = ["Jan", "Feb", "Mar", "Apr", "May"];
  let noShowsSeriesData = [8, 11, 8, 9, 13];
  let lateArrivalsSeriesData = [5, 7, 9, 6, 5];
  let rescheduledSeriesData = [6, 8, 5, 10, 8];

  if (disruptionsData && disruptionsData.length > 0) {
    noShowsCategories = disruptionsData.map((item) => String(item.month || ""));
    noShowsSeriesData = disruptionsData.map((item) => Number(item.noShows ?? 0));
    lateArrivalsSeriesData = disruptionsData.map((item) => Number(item.lateArrivals ?? 0));
    rescheduledSeriesData = disruptionsData.map((item) => Number(item.rescheduled ?? 0));
  }

  const maxDisruptions = Math.max(
    ...noShowsSeriesData,
    ...lateArrivalsSeriesData,
    ...rescheduledSeriesData,
    14
  );

  const noShowsChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "45%", borderRadius: 3 } },
    colors: ["#EF4444", "#F59E0B", "#3B82F6"],
    xaxis: {
      categories: noShowsCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    yaxis: {
      min: 0, max: maxDisruptions, tickAmount: 7,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    legend: { position: "top", horizontalAlign: "left", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const noShowsChartSeries = [
    { name: "No shows", data: noShowsSeriesData },
    { name: "Late arrivals", data: lateArrivalsSeriesData },
    { name: "Rescheduled", data: rescheduledSeriesData },
  ];

  // 4. Matrix Heatmap: Time-slot Popularity Parsing
  const slotPopArr = Array.isArray(trendsData?.timeSlotPopularity)
    ? trendsData.timeSlotPopularity
    : null;

  let matrixTimes = [
    "10-11", "11-12", "12-13", "13-14", "14-15",
    "15-16", "16-17", "17-18", "18-19", "19-20",
  ];
  let matrixDays = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
  let matrixData = [
    ["2", "10+", "5", "10+", "5", "10+", "2"],
    ["10+", "5", "2", "10+", "2", "2", "2"],
    ["5", "5", "10+", "5", "2", "2", "2"],
    ["2", "2", "5", "5", "2", "10+", "10+"],
    ["5", "2", "2", "2", "10+", "5", "2"],
    ["10+", "5", "2", "2", "2", "10+", "10+"],
    ["10+", "10+", "10+", "10+", "5", "2", "5"],
    ["10+", "10+", "10+", "10+", "10+", "10+", "10+"],
    ["10+", "5", "2", "10+", "2", "2", "2"],
    ["10+", "5", "2", "5", "2", "2", "5"],
  ];

  if (slotPopArr && slotPopArr.length > 0) {
    matrixTimes = slotPopArr.map((item) => String(item.slot || ""));
    if (Array.isArray(slotPopArr[0]?.days)) {
      matrixDays = slotPopArr[0].days.map((d) => String(d.day || ""));
    }
    matrixData = slotPopArr.map((slotObj) => {
      if (Array.isArray(slotObj?.days)) {
        return slotObj.days.map((d) => (d.count > 10 ? "10+" : String(d.count)));
      }
      return ["0", "0", "0", "0", "0", "0", "0"];
    });
  }

  const getCellClass = (val) => {
    const num = parseInt(val, 10);
    if (val === "2" || num <= 5) return "cell-fewer";
    if (val === "5" || (num > 5 && num <= 15)) return "cell-medium";
    return "cell-more";
  };

  const handleExportExcel = () => {
    const tableData = appointmentTable?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Date & time": row?.appointmentDateTime
        ? moment(new Date(row?.appointmentDateTime)).format("DD MMM YYYY, HH:mm")
        : "",
      Vet: row?.vetName ?? "",
      Pet: row?.petName ?? "",
      "Case ID": row?.caseId ?? "",
      "Service type": row?.serviceType ?? "",
      "Visit type": row?.visitType ?? "",
      "Consultation mode": row?.typeOfConsult ?? "",
      Revenue: row?.revenue ?? 0,
      Status: row?.status ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Appointment Trends");
    XLSX.writeFile(workbook, `Appointment_Trends_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "appointments",
      page: currentPage,
      itemSize: rowsPerPage,
      granularity: selectedPeriod || "week",
    };

    if (dateRange) {
      const parts = dateRange.split(" to ");
      if (parts.length === 2) {
        reqObj.startDate = moment(parts[0], ["YYYY-MM", "YYYY-MM-DD"]).startOf("month").format("YYYY-MM-DD");
        reqObj.endDate = moment(parts[1], ["YYYY-MM", "YYYY-MM-DD"]).endOf("month").format("YYYY-MM-DD");
      }
    }

    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) reqObj.vetName = [searchValues?.vetName];
    if (searchValues?.petName?.length > 0) reqObj.petName = [searchValues?.petName];
    if (searchValues?.caseId?.length > 0) reqObj.caseId = [searchValues?.caseId];
    if (searchValues?.serviceType?.length > 0) reqObj.serviceType = searchValues?.serviceType?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Appointment Date")) {
      reqObj.startDate = moment(new Date(filterValues?.values?.["Start Date"] ?? new Date())).format("YYYY-MM-DD");
      reqObj.endDate = moment(new Date(filterValues?.values?.["End Date"] ?? new Date())).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Visit Type")) reqObj.visitType = filterValues?.values?.["Visit Type"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Consultation Mode")) reqObj.typeOfConsult = filterValues?.values?.["Consultation Mode"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Booking Type")) reqObj.modeOfBooking = filterValues?.values?.["Booking Type"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Scheduled/Immediate")) reqObj.immediate = filterValues?.values?.["Scheduled/Immediate"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Rescheduled")) reqObj.rescheduled = filterValues?.values?.["Rescheduled"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Status")) reqObj.status = filterValues?.values?.["Status"]?.map((li) => li?.value);
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getAppointmentsTableReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (appointmentTable?.data ?? [])?.map((li) => ({
      ...li,
      appointmentDateTime:
        li?.appointmentDateTime?.length > 0
          ? moment(new Date(li?.appointmentDateTime)).format("DD MMM YYYY, HH:mm")
          : null,
      rescheduled: (li?.rescheduled ?? "")?.toString(),
      typeOfConsult: li?.typeOfConsult === "Home" ? "Home Visit" : li?.typeOfConsult,
      revenue: Number(li?.revenue ?? 0)?.toFixed(2),
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getVetList = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.vetName)?.filter(Boolean));
  const getPetList = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.petName)?.filter(Boolean));
  const getCaseIds = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.caseId?.toString())?.filter(Boolean));

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  const getAssignedUserList = () => {
    if (!appointmentTable?.data) return [];
    const reqList = (appointmentTable?.data ?? [])
      ?.flatMap((li) => li?.assignedUsers)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.filter(Boolean)?.map((li) => {
      const reqList = vets?.vets?.find((vl) => vl?.doctorId === li);
      return { name: reqList?.name, value: li };
    });
    return reqDropList;
  };

  const getDropListByType = (typ) => {
    return typ === "Visit Type"
      ? visitTypes
      : typ === "Consultation Mode"
        ? typeList
        : typ === "Booking Type"
          ? typeAppointemnt
          : typ === "Scheduled/Immediate"
            ? scheduleImmediateList
            : typ === "Rescheduled"
              ? yesNoBoolList
              : typ === "Status"
                ? appointmentStatusList
                : typ === "Assigned Users"
                  ? getAssignedUserList()
                  : [];
  };

  return (
    <div className="appointment-trends-container">
      {/* Title */}
      <h2 className="appointment-trends-title">Appointment trends</h2>

      {/* Top Filter Bar */}
      <div className="appointment-trends-filter-bar">
        <Select
          size="small"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="2025-08 to 2026-08">2025-08 to 2026-08</MenuItem>
          <MenuItem value="2026-01 to 2026-08">2026-01 to 2026-08</MenuItem>
        </Select>

        <div className="appointment-trends-period-group">
          {[
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
            { label: "Year", value: "year" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`appointment-trends-period-btn ${selectedPeriod === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="appointment-trends-export-group">
        <button type="button" className="appointment-trends-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="appointment-trends-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (4 Cards) */}
      <div className="appointment-trends-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="appointment-trends-card" key={idx}>
            <h4 className="appointment-trends-card-title">{card.title}</h4>
            <div className="appointment-trends-card-value-container">
              <h2 className="appointment-trends-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Line Chart: Volume vs last year */}
      <div className="appointment-trends-chart-card">
        <div className="appointment-trends-chart-header">
          Appointment volume, vs last year
        </div>
        <Chart
          options={volumeChartOptions}
          series={volumeChartSeries}
          type="line"
          height={300}
        />
      </div>

      {/* Side-by-side Grid: No-shows + Time-slot popularity */}
      <div className="appointment-trends-charts-grid">
        <div className="appointment-trends-chart-card">
          <div className="appointment-trends-chart-header">
            No-shows, late arrivals & reschedules
          </div>
          <Chart
            options={noShowsChartOptions}
            series={noShowsChartSeries}
            type="bar"
            height={320}
          />
        </div>

        <div className="appointment-trends-chart-card">
          <div className="appointment-trends-chart-header">
            Time-slot popularity
          </div>
          <div className="appointment-trends-matrix-legend">
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span className="appointment-trends-legend-box" style={{ backgroundColor: "#dbeafe" }}></span> Fewer
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span className="appointment-trends-legend-box" style={{ backgroundColor: "#60a5fa" }}></span> Medium
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span className="appointment-trends-legend-box" style={{ backgroundColor: "#1d4ed8" }}></span> More
            </span>
          </div>
          <table className="appointment-trends-matrix-table">
            <thead>
              <tr>
                <th></th>
                {matrixDays.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixTimes.map((t, rIdx) => (
                <tr key={t}>
                  <td style={{ color: "#6b7280", fontWeight: 500 }}>{t}</td>
                  {matrixData[rIdx].map((cVal, cIdx) => (
                    <td key={cIdx} className={getCellClass(cVal)}>
                      {cVal}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drill-down Section */}
      <div className="appointment-trends-drilldown-header">Drill-down — appointments</div>

      {/* Filter Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? []}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getCaseIds()}
              value={searchValues?.caseId ?? []}
              name="caseId"
              handleChange={onSearchChange}
              label="Case ID"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={serviceTypes}
              value={searchValues?.serviceType ?? []}
              name="serviceType"
              handleChange={onSearchChange}
              label="Service Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appointmentTable?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default AppointmentsTableReport;
